import {kapitelDateString, kapitelDateTimeString, kapitelMonthString, kapitelYearString} from "@/graphql/kapitelTypes";
import {Employee} from "@/graphql/generated/graphql";
import date, {isSameMonth} from "@/helper/datetime/date";
import {computed, ComputedRef, ref, Ref} from "vue";
import {CalendarMonth, generateCalendarMonth, generateCalendarMonths} from "@/helper/calendar/calendarMonth";
import month, {getKapitelYearStringFromKapitelMonthString} from "@/helper/datetime/month";
import {
    getMostRecentPublishedMonth,
    MONTH_KPI_MONTH_BACK,
    SHARE_PROFIT_DISTRIBUTION_EFFECTIVE_START_DATE
} from "@/helper/monthKPI/monthKPI";
import {useAuthStore} from "@/store/auth";

export type ApexMonthRangerOptions<RenderSet> = {
    min? : kapitelMonthString | null,
    max?: kapitelMonthString | null,
    selected?: kapitelMonthString | null,
    renderer: (calendarMonth: CalendarMonth) => RenderSet
}

export class ApexMonthRanger<RenderSet> {
    static MinDefault() { return month.sub(this.MaxDefault(), MONTH_KPI_MONTH_BACK) }
    static MaxDefault() { return month.getCurrentKapitelMonth() }

    public readonly min: kapitelMonthString
    public readonly max: kapitelMonthString

    public readonly allCalendarMonths: CalendarMonth[]
    public readonly allMonths: kapitelMonthString[]


    public readonly initialSelection: kapitelMonthString
    public readonly initialSelectedIndex = computed(() => this.allMonths.indexOf(this.initialSelection))

    private readonly selected: Ref<kapitelMonthString>
    public selectedCalendarMonth = computed(() => generateCalendarMonth(this.selected.value))
    public selectedMonth = computed(() => this.selectedCalendarMonth.value.getId())
    public readonly selectedIndex = computed(() => this.allMonths.indexOf(this.selected.value))

    private renderer: (calendarMonth: CalendarMonth) => RenderSet
    private renderSets = new Map<kapitelDateString, ComputedRef<RenderSet>>()
    public allMonthsRenderSets: ComputedRef<RenderSet>[]
    public selectedRenderSet : ComputedRef<RenderSet> = computed(() => (this.renderSets.get(this.selected.value) as ComputedRef<RenderSet>).value)

    constructor({
                    min = null,
                    max = null,
                    selected = null,
                    renderer
    } : ApexMonthRangerOptions<RenderSet>) {
        this.max = max || ApexMonthRanger.MaxDefault()
        this.min = min || ApexMonthRanger.MinDefault()

        this.allCalendarMonths = generateCalendarMonths(this.min, undefined, this.max)
        this.allMonths = this.allCalendarMonths.map(calendarMonth => calendarMonth.getId())

        this.initialSelection = selected || this.max

        this.selected = ref(this.initialSelection)
        this.select(this.initialSelection)

        this.renderer = renderer;

        // fill up render sets
        this.allCalendarMonths
            .forEach(
                (calendarMonth: CalendarMonth) => this.renderSets.set(
                    calendarMonth.getId(),
                    computed(() => this.renderer(calendarMonth))
                )
            );

        this.allMonthsRenderSets = this.renderSets.values().toArray()
    }

    select(month: kapitelMonthString) {
        if (!this.isValidMonth(month)) {
            return
        }
        this.selected.value = month
    }
    selectIndex(i: number) {
        this.select(this.getMonth(i))
    }

    getMonth(i: number) {
        return this.allMonths[i]
    }

    getRenderSet(i: number) {
        return this.renderSets.get(this.getMonth(i))
    }

    isValidMonth(month: kapitelDateString) {
        return !date.isBefore(month, this.min) && !date.isAfter(month, this.max)
    }

    getMonthLabels(format:string = 'MMM') {
        return this.allCalendarMonths.map(cm => cm.format(format))
    }

    getYears() : kapitelYearString[] {
        const startYear = month.getKapitelYearStringFromKapitelMonthString(this.min)
        const endYear = month.getKapitelYearStringFromKapitelMonthString(this.max)
        return startYear === endYear ? [endYear] : [startYear, endYear]
    }
}

export const MonthKPIApexMonthRangerFactory =<RenderSet> (
    {
        min = null,
        max = null,
        selected = null,
        renderer
    } : ApexMonthRangerOptions<RenderSet>
) => {
    const firstMonth = SHARE_PROFIT_DISTRIBUTION_EFFECTIVE_START_DATE
    let lastMonth = month.sub(month.getCurrentKapitelMonth(), 1)
    if (!useAuthStore().isImpersonating()) {
        lastMonth = month.min(getMostRecentPublishedMonth(), lastMonth)
    }

    if (!selected) {
        // default selection: last month
        selected = lastMonth
    }

    if (!max) {
        // allow lastMonth or two of the previous month in the default timeframe - after that, switch to -3 / +2
        max = date.isAfter(selected, month.sub(lastMonth, 2)) || date.isSameDay(selected, month.sub(lastMonth, 2))
            ? lastMonth
            : month.add(selected, 2)
    }

    if (!min) {
        // constrain lower border by firstMonth
        min = month.max(month.sub(max, MONTH_KPI_MONTH_BACK), firstMonth)
    }

    // check for max >= selected >= min >= firstMonth
    if (
        date.isAfter(firstMonth, min) ||
        date.isAfter(min, selected) ||
        date.isAfter(selected, max)
    ) {
        throw new Error("wrong config for month ranger: " + min + selected + max)
    }

    return new ApexMonthRanger({min, max, selected, renderer})
}
